import React, { useEffect, useMemo, useRef, useState } from 'react';
import DottedMap from 'dotted-map';
import DOMPurify from 'dompurify';
import { stats } from '../data/mock';

const WorldMap = () => {
  const svgString = useMemo(() => {
    const map = new DottedMap({ height: 54, grid: 'diagonal' });
    const svg = map.getSVG({
      radius: 0.35,
      color: '#5aad7f',
      shape: 'circle',
      backgroundColor: 'transparent',
    });
    // Sanitise defensively even though dotted-map produces trusted SVG.
    return DOMPurify.sanitize(svg, { USE_PROFILES: { svg: true, svgFilters: true } });
  }, []);

  return (
    <div
      className="w-full h-auto select-none pointer-events-none opacity-80"
      // eslint-disable-next-line react/no-danger -- svgString is sanitised by DOMPurify above
      dangerouslySetInnerHTML={{ __html: svgString }}
    />
  );
};

const Counter = ({ value }) => {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const target = parseInt(value.replace(/\D/g, ''), 10) || 0;
    const suffix = value.includes('+') ? '+' : '';

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started.current) {
            started.current = true;
            const duration = 1400;
            const step = 30;
            let current = 0;
            const inc = target / (duration / step);
            const timer = setInterval(() => {
              current += inc;
              if (current >= target) {
                setDisplay(`${target}${suffix}`);
                clearInterval(timer);
              } else {
                setDisplay(`${Math.floor(current)}${suffix}`);
              }
            }, step);
          }
        });
      },
      { threshold: 0.3 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [value]);

  return (
    <span ref={ref} className="tabular-nums">
      {display || '0'}
    </span>
  );
};

const Stats = () => {
  return (
    <section className="relative bg-[#e2ede6] py-10 lg:py-16 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        <div className="relative min-h-[420px]">
          {/* World map background */}
          <div className="absolute inset-0 flex items-center justify-end opacity-90">
            <div className="w-full lg:w-[75%]">
              <WorldMap />
            </div>
          </div>

          {/* Stats card */}
          <div className="relative z-10 w-full max-w-md lg:max-w-sm">
            <div className="bg-gradient-to-br from-[#0f5c3d] to-[#137a52] text-white p-8 lg:p-10 rounded-[6px] shadow-2xl">
              <div className="grid grid-cols-2 gap-x-6 gap-y-10">
                {stats.map((s) => (
                  <div key={s.label}>
                    <div
                      className="text-4xl lg:text-5xl font-bold leading-none"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      <Counter value={s.value} />
                    </div>
                    <div className="mt-2 text-sm text-white/85">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Stats;
